import { useState } from "react";
import { useAdmin } from "../context/AdminContext";

export function WorkflowPanel() {
  const { workflow } = useAdmin();
  
  const [path, setPath] = useState('/v1/workflows');
  const [method, setMethod] = useState<string>('GET');
  const [body, setBody] = useState('');
  const [bodyError, setBodyError] = useState<string | null>(null);

  const handleExecute = async () => {
    setBodyError(null);
    
    let parsedBody = undefined;
    if (body.trim() && method !== 'GET' && method !== 'HEAD') {
      try {
        parsedBody = JSON.parse(body);
      } catch (err) {
        setBodyError('Invalid JSON');
        return;
      }
    }

    try {
      await workflow.proxy.executeRequest(path, method, parsedBody);
    } catch (err) {
      // Error is handled in the hook
    }
  };

  const handleClear = () => {
    workflow.proxy.clearState();
    setBodyError(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Workflow (N8N Proxy)
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Execute N8N API requests through the workflow proxy
        </p>
      </div>

      {/* Request Form */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Proxy Request
        </h3>

        {/* Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Method
          </label>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
            <option value="PATCH">PATCH</option>
          </select>
        </div>

        {/* Path */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            N8N API Path
          </label>
          <input
            type="text"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="/v1/workflows"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-mono text-sm"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Full N8N path after /api (e.g., /v1/workflows, /v1/executions, /v1/workflows/123)
          </p>
        </div>

        {/* Body (for non-GET requests) */}
        {method !== 'GET' && method !== 'HEAD' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Request Body (JSON)
            </label>
            <textarea
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setBodyError(null);
              }}
              placeholder='{"name": "My Workflow"}'
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-mono text-sm"
            />
            {bodyError && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{bodyError}</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={handleExecute}
            disabled={workflow.proxy.loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {workflow.proxy.loading ? 'Executing...' : 'Execute'}
          </button>
          <button
            onClick={handleClear}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Error Display */}
      {workflow.proxy.error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">
            Error
          </h4>
          <p className="text-sm text-red-700 dark:text-red-300 font-mono">
            {workflow.proxy.error}
          </p>
        </div>
      )}

      {/* Result Display */}
      {workflow.proxy.result && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Response
          </h3>
          <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded-md overflow-auto max-h-96 text-xs font-mono text-gray-900 dark:text-gray-100">
            {JSON.stringify(workflow.proxy.result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

